/**
 * 이메일 HTML 본문 미리보기 유틸 (ID-226).
 *
 * BO 운영자가 입력한 HTML 본문(NotificationTemplate.is_html=true)을 폼에서 미리 볼 수 있게
 * 1) 변수 토큰을 예시 값으로 치환하고 2) DOMPurify 로 sanitize 한 뒤 렌더한다.
 *
 * ★ sanitize 는 운영자 입력을 신뢰 최소화로 다룬다 — `<script>`/이벤트 핸들러(onclick…)/
 *   `javascript:` 등 활성 콘텐츠를 제거한다. 실제 발송 HTML 은 BE 가 별도 관리하며, 이 sanitize
 *   는 어디까지나 BO 미리보기 패널의 XSS 차단용(운영자 화면 보호)이다.
 * ★ 변수 토큰 인식: `#{var}`(BE 정본) · `{{var}}`(참고소스) · `{rc_xxx}`(godomall, 단일중괄호).
 *   예시 값이 있으면 치환, 없으면 토큰을 그대로 둔다(운영자가 미입력 변수를 식별).
 */
import DOMPurify, { type Config } from 'dompurify';
import type { TemplateVariable } from './kakaoTemplate';

/**
 * 미리보기에서 인식하는 변수 토큰 — 세 형태를 한 번에 매칭한다.
 *  1) `#{var}`      — BE 렌더러 정본(AlimtalkTemplateRenderer PLACEHOLDER)
 *  2) `{{var}}`     — 참고소스 토큰(BO 작성 호환)
 *  3) `{rc_xxx}`    — godomall 단일중괄호(ID-226 이메일 본문 정본). `rc_` 프리픽스 키만.
 * 캡처 그룹: m[1]=#{}, m[2]={{}}, m[3]={rc_xxx}.
 */
const PREVIEW_TOKEN_RE =
  /#\{([A-Za-z0-9_]+)\}|\{\{\s*([A-Za-z0-9_]+)\s*\}\}|\{(rc_[A-Za-z0-9_]+)\}/g;

/**
 * 본문에서 미리보기 변수 키를 순서대로(중복 제거) 추출한다 — `#{}`·`{{}}`·`{rc_xxx}` 세 형태 모두.
 * EMAIL HTML 본문(godomall `{rc_xxx}` 토큰)에서도 변수 패널이 키를 잡도록 한다.
 * (기존 `extractVariableKeys`(#{var} 전용)와 별개 — 그건 알림톡/plain 경로 정본으로 보존.)
 */
export const extractPreviewKeys = (content: string): string[] => {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const match of content.matchAll(PREVIEW_TOKEN_RE)) {
    const key = (match[1] ?? match[2] ?? match[3] ?? '').trim();
    if (key && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  return keys;
};

/** HTML 컨텍스트에 안전하게 삽입하기 위한 텍스트 escape(예시 값은 사용자 데이터로 취급). */
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * 본문의 변수 토큰을 예시 값으로 치환한다.
 * - `html=true` 면 예시 값을 HTML escape(치환 값이 마크업으로 해석되지 않게).
 * - 예시 값이 없으면 원본 토큰을 보존(운영자가 미입력 변수를 식별).
 */
export const substituteTokens = (
  content: string,
  variables: ReadonlyArray<TemplateVariable>,
  html: boolean,
): string => {
  const exampleByKey = new Map(variables.map((v) => [v.key, v.example]));
  return content.replace(PREVIEW_TOKEN_RE, (whole, hashKey, braceKey, rcKey) => {
    const key = (hashKey ?? braceKey ?? rcKey ?? '') as string;
    const example = exampleByKey.get(key);
    if (example == null || example === '') return whole;
    return html ? escapeHtml(example) : example;
  });
};

/**
 * DOMPurify sanitize 설정 — 이메일 미리보기용. **allowlist 방식**(허용 태그/속성만 통과,
 * 나머지는 전부 제거)이라 `<script>`·`on*` 핸들러·미지정 태그는 자동으로 떨어진다. 이메일에
 * 흔한 정적 마크업(table/inline style/img/a)은 허용한다. `style` 속성은 인라인 CSS 이메일
 * 특성상 허용하되 DOMPurify 가 위험 패턴(expression/url(javascript:))을 정리한다.
 */
const SANITIZE_CONFIG: Config = {
  ALLOWED_TAGS: [
    'p', 'br', 'span', 'div', 'b', 'strong', 'i', 'em', 'u', 's', 'a', 'img',
    'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'blockquote',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'colgroup',
    'col', 'small', 'sup', 'sub', 'center', 'font',
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'style', 'width', 'height', 'align', 'valign',
    'bgcolor', 'color', 'border', 'cellpadding', 'cellspacing', 'colspan', 'rowspan',
    'target', 'rel', 'class',
  ],
  ALLOW_DATA_ATTR: false,
  // allowlist 외에도 위험 컨테이너는 명시 금지(이중 방어).
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'base', 'form', 'link', 'meta'],
};

/**
 * 위험 컨테이너 요소(`<script>`/`<style>`/`<iframe>` 등) 전체를 정규식으로 선제거한다.
 *
 * ★ 왜 정규식 pre-pass 인가: DOMPurify 는 DOM 파서에 의존하는데, 일부 비-브라우저 DOM 구현
 *   (예: 테스트 환경 happy-dom)에서 혼합 콘텐츠의 `<script>` 요소가 sanitize 를 통과하는
 *   엔진 차이가 관측된다. 실제 브라우저에서는 DOMPurify 만으로 충분하지만, 환경 무관하게
 *   가장 위험한 활성 콘텐츠를 확실히 제거하기 위해 DOM 파싱 전 텍스트 레벨에서 한 번 더 벗긴다
 *   (defense-in-depth). 이후 DOMPurify 가 속성/스킴/나머지 마크업을 정리한다.
 */
const DANGEROUS_BLOCK_RE =
  /<\s*(script|style|iframe|object|embed|noscript|template)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi;
/** 닫는 태그가 없는 self-closing/orphan 위험 태그(예: <script src=...>)도 제거. */
const DANGEROUS_OPEN_RE =
  /<\s*(script|style|iframe|object|embed|noscript|template|base|link|meta|form)\b[^>]*>/gi;
/** 인라인 이벤트 핸들러 속성(on*="…" / on*='…' / on*=value)을 제거. */
const EVENT_HANDLER_ATTR_RE =
  /\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
/** href/src 등에 박힌 javascript:·vbscript:·data:text/html 스킴을 무력화(빈 anchor 로). */
const DANGEROUS_SCHEME_RE =
  /\s+(href|src|xlink:href)\s*=\s*(?:"\s*(?:javascript|vbscript|data)\s*:[^"]*"|'\s*(?:javascript|vbscript|data)\s*:[^']*'|(?:javascript|vbscript|data)\s*:[^\s>]+)/gi;

/**
 * DOMPurify 이전 텍스트 레벨 선제거(환경 무관 defense-in-depth):
 *  1) 위험 컨테이너 요소 통째 제거 → 2) 인라인 on* 핸들러 제거 → 3) 위험 스킴 href/src 제거.
 */
const stripDangerousBlocks = (html: string): string =>
  html
    .replace(DANGEROUS_BLOCK_RE, '')
    .replace(DANGEROUS_OPEN_RE, '')
    .replace(EVENT_HANDLER_ATTR_RE, '')
    .replace(DANGEROUS_SCHEME_RE, '');

/**
 * 이메일 HTML 본문 미리보기 문자열 생성:
 *  1) 변수 토큰을 예시 값으로 치환(HTML escape) → 2) DOMPurify sanitize.
 * 반환값은 `dangerouslySetInnerHTML` 에 안전하게 넣을 수 있는 정화된 HTML.
 */
export const renderEmailHtmlPreview = (
  body: string,
  variables: ReadonlyArray<TemplateVariable>,
): string => {
  const substituted = substituteTokens(body, variables, true);
  // 1) 위험 컨테이너(script/style/iframe…) 텍스트 선제거 → 2) DOMPurify allowlist sanitize.
  const prestripped = stripDangerousBlocks(substituted);
  // SANITIZE_CONFIG 에 RETURN_DOM* 옵션이 없으므로 sanitize 는 string 을 반환한다.
  return DOMPurify.sanitize(prestripped, SANITIZE_CONFIG);
};

/**
 * plain-text 미리보기(is_html=false): 변수만 치환하고 마크업 해석 없이 그대로 노출한다.
 * 호출부에서 `white-space: pre-wrap` 텍스트로 렌더한다(escape 불필요 — React 가 텍스트로 처리).
 */
export const renderPlainPreview = (
  body: string,
  variables: ReadonlyArray<TemplateVariable>,
): string => substituteTokens(body, variables, false);
